import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  Filter,
  RefreshCw,
  ExternalLink,
  FileCode,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { dashboardApi } from '../../services/iso20022Api';
import {
  formatDate,
  getStatusBadgeClass,
  getDirectionBadgeClass,
  truncate,
  debounce
} from '../../utils/helpers';
import XmlViewerModal from '../../components/iso20022/XmlViewerModal';

const StatusRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    direction: '',
    startDate: '',
    endDate: '',
    search: ''
  });
  const [xmlModalOpen, setXmlModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);

  const loadStatusRequests = async () => {
    try {
      setLoading(true);
      const data = await dashboardApi.getStatusRequests(filters);
      console.log('[Load Status Requests] Count:', data?.length ?? 0);
      setRequests(data);
    } catch (error) {
      console.error('[Load Status Requests] Error:', error);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatusRequests();
  }, [filters]);

  const handleFilterChange = debounce((name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  }, 300);

  const clearFilters = () => {
    setFilters({
      status: '',
      direction: '',
      startDate: '',
      endDate: '',
      search: ''
    });
  };

  const handleViewXml = (request) => {
    setSelectedRequest(request);
    setXmlModalOpen(true);
  };

  const getRequestStatusIcon = (status, signatureOk) => {
    if (signatureOk === false) {
      return <XCircle className="w-5 h-5 text-danger-600" />;
    }
    
    switch (status?.toUpperCase()) {
      case 'SENT':
      case 'SIGNED':
        return <CheckCircle2 className="w-5 h-5 text-success-600" />;
      case 'RECEIVED':
        return <CheckCircle2 className="w-5 h-5 text-blue-600" />;
      case 'PROCESSING':
        return <Clock className="w-5 h-5 text-warning-600 animate-pulse" />;
      case 'FAILED':
      case 'REJECTED':
        return <XCircle className="w-5 h-5 text-danger-600" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-400" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Payment Status Requests</h1>
          <p className="text-gray-600 mt-1">View pacs.028 status request messages</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={loadStatusRequests}
            disabled={loading}
            className="btn-primary flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Filter className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
          </div>
          <button
            onClick={clearFilters}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Clear All
          </button>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by Message ID, End-to-End ID..."
                  className="input pl-10"
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                className="select"
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              >
                <option value="">All Statuses</option>
                <option value="SIGNED">Signed</option>
                <option value="SENT">Sent</option>
                <option value="RECEIVED">Received</option>
                <option value="PROCESSING">Processing</option>
                <option value="FAILED">Failed</option>
              </select>
            </div>

            {/* Direction */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Direction
              </label>
              <select
                className="select"
                value={filters.direction}
                onChange={(e) => setFilters(prev => ({ ...prev, direction: e.target.value }))}
              >
                <option value="">All Directions</option>
                <option value="OUTGOING_SIGNED">Outgoing</option>
                <option value="INCOMING">Incoming</option>
              </select>
            </div>

            {/* Date Range */}
            <div className="lg:col-span-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date Range
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="date"
                  className="input text-sm"
                  value={filters.startDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                />
                <span className="text-gray-500">to</span>
                <input
                  type="date"
                  className="input text-sm"
                  value={filters.endDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Results ({requests.length})
            </h2>
          </div>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center space-y-4">
                <RefreshCw className="w-8 h-8 text-primary-600 animate-spin" />
                <p className="text-gray-600">Loading status requests...</p>
              </div>
            </div>
          ) : requests.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-900 font-medium mb-2">No status requests found</p>
                <p className="text-gray-600">Try adjusting your filters</p>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Message ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Original E2E ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Original TX ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Instructing Agent
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Direction
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Signature
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {requests.map((req) => (
                    <tr key={req.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono font-medium text-gray-900">
                          #{req.id}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono text-gray-900">
                          {truncate(req.msgId, 30)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono text-blue-600 font-medium">
                          {truncate(req.orglEndToEndId, 30)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-mono text-gray-600">
                          {truncate(req.orglTxId, 30) || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {req.instgAgtName || req.instgAgtId || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`badge ${getDirectionBadgeClass(req.direction)}`}>
                          {req.direction}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="inline-flex items-center space-x-2">
                          {getRequestStatusIcon(req.status, req.signatureOk)}
                          <span className={`badge ${getStatusBadgeClass(req.status)}`}>
                            {req.status || 'UNKNOWN'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {req.signatureOk === true ? (
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-success-100" title="Signature valid">
                            <svg className="w-5 h-5 text-success-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </span>
                        ) : req.signatureOk === false ? (
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-danger-100" title="Signature invalid">
                            <svg className="w-5 h-5 text-danger-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(req.createdAt, 'PPp')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {/* Quick XML View */}
                          {(req.rawXml || req.signedXml) && (
                            <button
                              onClick={() => handleViewXml(req)}
                              className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 transition-colors"
                              title="View XML"
                            >
                              <FileCode className="w-4 h-4" />
                            </button>
                          )}
                          <Link
                            to={`/iso20022/status-requests/${req.id}`}
                            className="text-primary-600 hover:text-primary-900 inline-flex items-center space-x-1"
                          >
                            <span>View</span>
                            <ExternalLink className="w-3 h-3" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* XML Viewer Modal */}
      {selectedRequest && (
        <XmlViewerModal
          isOpen={xmlModalOpen}
          onClose={() => {
            setXmlModalOpen(false);
            setSelectedRequest(null);
          }}
          xmlContent={selectedRequest.rawXml || selectedRequest.signedXml}
          fileName={`status-request-${selectedRequest.id}-pacs028.xml`}
          title={`Status Request XML - #${selectedRequest.id}`}
        />
      )}
    </div>
  );
};

export default StatusRequests;